"use client";

import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import axiosInstance from "../../helpers/Axios";

// ----------------- Styled Components -----------------
const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

// ----------------- Component -----------------
const UploadUserSubscriptions = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);

  const handleFileChange = (event) => {
    setCsvFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!csvFile) {
      alert("Please upload a CSV or Excel file.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("csv_file", csvFile);

    try {
      const response = await axiosInstance.post(
        `${baseURL}/api/trail/create-user-subscription/`,
        formData
      );

      // Normalize backend structure
      const normalized = {
        summary: response.data?.data?.summary ?? {},
        details: response.data?.data?.details ?? [],
        status: response.data?.status ?? "unknown",
        message: response.data?.message ?? "",
        errors: response.data?.errors ?? [],
      };

      setResponseData(normalized);
    } catch (error) {
      console.error("Error uploading file:", error);
      setResponseData({
        error:
          error.response?.data?.error ||
          error.response?.data?.detail ||
          "Upload failed! Please try again.",
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCsvFile(null);
    setResponseData(null);
  };

  // ----------------- Render Results -----------------
  const renderResults = () => {
    if (!responseData?.details?.length) return null;

    return (
      <div className="mt-4 border border-gray-200 p-3 rounded-lg">
        <Typography variant="h6" className="mb-3 text-gray-800 font-medium">
          Upload Details ({responseData.details.length})
        </Typography>
        <div className="max-h-80 overflow-y-auto pr-1">
          {responseData.details.map((r, index) => {
            let colorScheme;

            switch (r.status) {
              case "success":
                colorScheme = {
                  bg: "bg-green-50",
                  border: "border-green-200",
                  text: "text-green-700",
                  icon: <CheckCircleIcon />,
                };
                break;
              case "updated":
                colorScheme = {
                  bg: "bg-blue-50",
                  border: "border-blue-200",
                  text: "text-blue-700",
                  icon: <CheckCircleIcon />,
                };
                break;
              case "skipped":
                colorScheme = {
                  bg: "bg-yellow-50",
                  border: "border-yellow-200",
                  text: "text-yellow-700",
                  icon: <ErrorIcon />,
                };
                break;
              case "failed":
              default:
                colorScheme = {
                  bg: "bg-red-50",
                  border: "border-red-200",
                  text: "text-red-700",
                  icon: <ErrorIcon />,
                };
            }

            return (
              <div
                key={index}
                className={`${colorScheme.bg} p-3 rounded-lg mb-2 border ${colorScheme.border}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 ${colorScheme.text}`}>
                    {colorScheme.icon}
                    <p className="font-semibold text-sm">
                      Row {r.row}: {r.username} → {r.plan}
                    </p>
                  </div>
                  <p className="text-xs font-medium uppercase bg-white border px-2 py-1 rounded">
                    {r.status}
                  </p>
                </div>
                {r.reason && (
                  <p className="mt-2 text-sm text-gray-600">Reason: {r.reason}</p>
                )}
                {r.message && (
                  <p className="mt-2 text-sm text-gray-600">{r.message}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ----------------- Main UI -----------------
  return (
    <StyledBox>
      <FormContainer>
        <Typography
          variant="h4"
          className="text-center mb-6 font-semibold text-gray-800"
        >
          Create Bulk User Subscription 
        </Typography>
        <form onSubmit={handleSubmit} className="space-y-6">
          <TextField
            type="file"
            label="CSV or Excel File"
            accept=".csv"
            onChange={handleFileChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" />
              ),
            }}
          />
          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            className="py-2.5"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                <GroupAddIcon className="w-5 h-5 mr-2" /> Upload Subscriptions
              </>
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <Typography variant="h6" className="text-gray-800">
                Upload Summary
              </Typography>
              <IconButton
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <DialogContent className="px-6 py-5">
            {responseData ? (
              <>
                {responseData.message && (
                  <div className="mb-3 text-gray-600">
                    <Typography variant="subtitle1">
                      {responseData.message}
                    </Typography>
                  </div>
                )}

                {responseData.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 p-3 rounded-lg mb-3">
                    {responseData.errors.map((e, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        className="text-red-600"
                      >
                        {e}
                      </Typography>
                    ))}
                  </div>
                )}

                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <ResultCard>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-500"
                    >
                      Created
                    </Typography>
                    <Typography variant="h6" className="text-green-600">
                      {responseData.summary?.created ?? 0}
                    </Typography>
                  </ResultCard>
                  <ResultCard>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-500"
                    >
                      Updated
                    </Typography>
                    <Typography variant="h6" className="text-blue-600">
                      {responseData.summary?.updated ?? 0}
                    </Typography>
                  </ResultCard>
                  <ResultCard>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-500"
                    >
                      Failed
                    </Typography>
                    <Typography variant="h6" className="text-red-600">
                      {responseData.summary?.failed ?? 0}
                    </Typography>
                  </ResultCard>
                  <ResultCard>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-500"
                    >
                      Skipped
                    </Typography>
                    <Typography variant="h6" className="text-yellow-600">
                      {responseData.summary?.skipped ?? 0}
                    </Typography>
                  </ResultCard>
                  <ResultCard>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-500"
                    >
                      Total
                    </Typography>
                    <Typography variant="h6">
                      {responseData.summary?.total ?? 0}
                    </Typography>
                  </ResultCard>
                </div>

                {renderResults()}
              </>
            ) : (
              <Typography>No data received from server.</Typography>
            )}
          </DialogContent>
          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <StyledButton onClick={handleClose} variant="contained">
                Close
              </StyledButton>
            </div>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default UploadUserSubscriptions;

